import { createStore } from 'vuex';
import axios from 'axios';
import user from "@/store/user.store";
import selection from "@/store/selection.store";
import collections from "@/store/collections.store";
import oqlBuilder from "@/store/oqlBuilder.store";
import query from "@/store/query.store";
import { entityConfigs } from '@/entityConfigs';
import { facetsByCategory } from '@/facetConfigUtils';
import { urlBase, axiosConfig } from '@/apiConfig';
import { api } from '@/api';
import { NEW_OQL_FLAG, NL_SEARCH_FLAG, pickOql } from '@/oqlForm';
import { hasBrowser } from '@/utils/env';

// oxjob #1689 Nuxt trial: SSR-safe localStorage read (the server has none).
const lsGet = (k) => (hasBrowser ? localStorage.getItem(k) : null);

// Conversion: 1 credit = $0.0001 (10,000 credits = $1)
const CREDIT_TO_USD = 0.0001;

export function formatUsd(dollars, decimals = 3) {
    if (dollars == null) return '$0.' + '0'.repeat(decimals);
    return '$' + Number(dollars).toFixed(decimals);
}

export function creditsToUsd(credits) {
    if (credits == null) return 0;
    return Math.round(credits * CREDIT_TO_USD * 10000) / 10000;
}

const stateDefaults = function () {
    const ret = {
        entityType: "works",
        activeFilter: null,
        activeFilterKey: null,
        newFilterKey: null,
        filterObjects: [],
        appliedFilterObjects: [],
        textSearch: "",
        page: 1,
        results: [],
        resultsObject: null,
        sort: null,
        responseTime: null,
        resultsCount: null,
        isLoading: false,
        snackbarIsOpen: false,
        snackbarMsg: "",
        snackbarIcon: null,
        snackbarColor: undefined,
        zoomId: null,
        // The in-app API viewer (App.vue, oxjob #1246).
        apiDialogIsOpen: false,
        apiDialogUrl: "",
        showFiltersDrawer: false,
        facetZoom: null,
        facetsListDialogIsOpen: false,
        exportProgressUrl: null,
        filterOptionChipOpenMenu: null,
        useV2: false,
        // SERP page size (results per page). List and table view keep INDEPENDENT
        // sizes so changing one never bleeds into the other (table wants dense
        // pages, list wants fewer tall cards). Persisted to localStorage; list
        // defaults 10, table defaults 100. url.js getPerPage picks by current view;
        // a deep-link `per_page` is adopted into the active view's size as a session
        // override (persist:false) without overwriting the saved preference.
        serpPageSize: parseInt(lsGet('serp-page-size'), 10) || 10,
        serpTablePageSize: parseInt(lsGet('serp-table-page-size'), 10) || 100,
        // SERP results presentation (list vs table) — recipient-local CHROME, kept
        // OFF the URL (#492). This reactive store is the source of truth so the
        // toggle updates the UI with no navigation; persisted to localStorage
        // (key `oax.resultsView`, zd#8973). url.isTableView reads this; a legacy
        // inbound `?view=table` is seeded here then stripped (see Serp.vue).
        serpResultsView: lsGet('oax.resultsView') === 'table' ? 'table' : 'list',
        // The "Show API query" overlay (#492) — session-only chrome, never
        // persisted, off the URL. Seeded from a legacy `?view=api` link, then
        // stripped. (Replaces the vestigial `isApiEditorShowing` flag below.)
        serpShowApi: false,
        // SERP basic/advanced mode SESSION override (#492 Phase 2) — recipient-local
        // chrome, OFF the URL. null = use the durable per-device pref (localStorage
        // `serpMode`). Set by an explicit mode switch (reactive, no navigation) and
        // seeded session-only from a legacy inbound `?mode=` (then the param is
        // stripped, see Serp.vue) so a shared link never rewrites a recipient's pref.
        serpModeOverride: null,
        // SERP sidebar group-by widget layout, keyed by entity type (#492 Phase 4).
        // Session-only chrome, OFF the URL (charter decision 33 — OQL can't represent
        // an ad-hoc widget layout; a real dashboard is the future home). An unset
        // entity falls back to entityConfigs `groupByDefaults` (see url.getGroupBy);
        // an empty array means the user cleared all widgets (distinct from unset).
        // Seeded session-only from a legacy inbound `?group_by=` (then stripped, see
        // Serp.vue) so a shared link no longer reproduces the sender's widget layout.
        serpGroupBy: {},
        isInitialLoad: true, // used to for bypassing cache on freshloads
        // Centralized query object - fetched once on page load, used by all view modes
        queryObject: null,
        queryObjectLoading: false,
        queryObjectError: null,
        // Structured validation error from the last OQL submit (#373), surfaced
        // inline in the OQL panel. Shape: {valid:false, errors:[{message,position,…}]}.
        oqlSubmitError: null,
        showEntityPageStats: true, // show metric rows + group-bys + works section on entity pages
        plans: [], // available plans loaded at app boot
        defaultApiMaxPerDay: 10000, // default credits per day for users without a plan
        rateLimitData: null,
        rateLimitLastFetchedAt: null,
        featureFlags: {},
        creditLimitDialogIsOpen: false,
    }
    return ret;
}


// Clear per-user store modules when the user logs out. Without this,
// signing in as user B in the same tab briefly renders user A's collections
// until something dispatches collections/fetchAll (security review M7).
const clearPerUserStateOnLogout = (store) => {
    store.subscribe((mutation) => {
        if (mutation.type === 'user/logout') {
            store.commit('collections/clear');
            // Per-user feature flags must not outlive the session — since
            // #464 Phase 3b the `legacy_ui` flag forks the whole interface,
            // so a stale copy would keep the legacy UI after logout.
            store.commit('setFeatureFlags', []);
        }
    });
};

// oxjob #1689 Nuxt trial: a factory, so the server can build one store per request.
export function createAppStore() {
return createStore({
    state: stateDefaults,
    modules: {
        user,
        selection,
        collections,
        oqlBuilder,
        query,
    },
    plugins: [clearPerUserStateOnLogout],
    mutations: {
        openApiDialog(state, url) {
            state.apiDialogUrl = url;
            state.apiDialogIsOpen = true;
        },
        setApiDialogIsOpen(state, isOpen) {
            state.apiDialogIsOpen = isOpen;
        },
        snackbar(state, arg) {
            state.snackbarIsOpen = true;
            if (typeof arg === "string") {
                state.snackbarMsg = arg;
            } else {
                state.snackbarMsg = arg.msg;
                state.snackbarColor = arg.color;
            }
        },
        closeSnackbar(state) {
            state.snackbarMsg = "";
            state.snackbarIsOpen = false;
            state.snackbarColor = undefined;
        },
        showCreditLimitDialog(state) {
            state.creditLimitDialogIsOpen = true;
        },
        closeCreditLimitDialog(state) {
            state.creditLimitDialogIsOpen = false;
        },
        setGlobalIsLoading(state, isLoading) {
            state.isLoading = !!isLoading;
        },
        toggleFiltersDrawer(state) {
            state.showFiltersDrawer = !state.showFiltersDrawer;
        },
        setZoomId(state, id) {
            state.zoomId = id;
        },
        setEntityType(state, entityType) {
            state.entityType = entityType;
        },
        setIsInitialLoad(state, isInitialLoad) {
            state.isInitialLoad = isInitialLoad;
        },
        setUseV2(state, value) {
            state.useV2 = value;
        },
        setShowEntityPageStats(state, value) {
            state.showEntityPageStats = value;
        },
        // persist:true for an explicit user pick (writes the durable preference);
        // persist:false for a session-only override adopted from a URL `per_page`.
        setSerpPageSize(state, { value, persist }) {
            state.serpPageSize = value;
            if (persist) localStorage.setItem('serp-page-size', String(value));
        },
        setSerpTablePageSize(state, { value, persist }) {
            state.serpTablePageSize = value;
            if (persist) localStorage.setItem('serp-table-page-size', String(value));
        },
        // #492: results presentation (list/table). persist:true writes the durable
        // localStorage pref; a bare/legacy load seeds the reactive state without it.
        setSerpResultsView(state, { value, persist }) {
            state.serpResultsView = value === 'table' ? 'table' : 'list';
            if (persist) {
                try { localStorage.setItem('oax.resultsView', state.serpResultsView); }
                catch (e) { /* private mode / quota — best-effort */ }
            }
        },
        // #492: the "Show API query" overlay. Session-only — never persisted.
        setSerpShowApi(state, value) {
            state.serpShowApi = !!value;
        },
        // #492 Phase 2: the basic/advanced/oql session override. Session-only — the
        // durable pref is localStorage `serpMode`, written separately on a switch.
        // 'advanced2' = the outline-builder experiment tab (2026-07-11, re #595).
        setSerpModeOverride(state, value) {
            state.serpModeOverride = (value === 'basic' || value === 'advanced' || value === 'advanced2' || value === 'oql') ? value : null;
        },
        // #492 Phase 4: the SERP sidebar group-by widget layout, per entity type.
        // Reassign the object so Vuex sees the change; store a copy so callers that
        // mutate the returned array (e.g. drag-reorder splice) can't corrupt state.
        setSerpGroupBy(state, { entityType, keys }) {
            state.serpGroupBy = {
                ...state.serpGroupBy,
                [entityType]: Array.isArray(keys) ? [...keys] : [],
            };
        },
        setQueryObject(state, queryObject) {
            state.queryObject = queryObject;
            state.queryObjectError = null;
        },
        setQueryObjectLoading(state, loading) {
            state.queryObjectLoading = loading;
        },
        setQueryObjectError(state, error) {
            state.queryObjectError = error;
            state.queryObject = null;
        },
        clearQueryObject(state) {
            state.queryObject = null;
            state.queryObjectError = null;
            state.queryObjectLoading = false;
        },
        setOqlSubmitError(state, error) {
            state.oqlSubmitError = error;
        },
        setPlans(state, plans) {
            state.plans = plans;
        },
        setDefaultApiMaxPerDay(state, value) {
            state.defaultApiMaxPerDay = value;
        },
        setRateLimitData(state, data) {
            state.rateLimitData = data;
            state.rateLimitLastFetchedAt = Date.now();
        },
        setFeatureFlags(state, flagNames) {
            const flags = {};
            for (const name of flagNames) {
                flags[name] = true;
            }
            // Merge in locally-persisted flags (for UI-defined flags not yet in backend)
            try {
                const localFlags = JSON.parse(localStorage.getItem('localFeatureFlags') || '[]');
                for (const name of localFlags) {
                    flags[name] = true;
                }
            } catch (e) { /* ignore parse errors */ }
            state.featureFlags = flags;
        },
    },
    actions: {
        async fetchPlans({ commit }) {
            try {
                const res = await axios.get(
                    `${urlBase.userApi}/plans`,
                    axiosConfig({ userAuth: true })
                );
                commit('setPlans', res.data.results || []);
                if (res.data.meta?.default_api_max_per_day) {
                    commit('setDefaultApiMaxPerDay', res.data.meta.default_api_max_per_day);
                }
            } catch (e) {
                console.error('Failed to fetch plans:', e);
            }
        },
        async fetchRateLimitData({ commit, state }, options) {
            const fresh = options?.fresh;
            const apiKey = state.user?.apiKey;
            if (!apiKey) return;
            try {
                const url = fresh
                    ? `${urlBase.api}/rate-limit?fresh=1`
                    : `${urlBase.api}/rate-limit`;
                const resp = await axios.get(url, axiosConfig());
                commit('setRateLimitData', resp.data?.rate_limit || null);
            } catch (e) {
                console.warn('Failed to fetch rate limit data:', e);
            }
        },
        async fetchQueryObject({ commit }, { entityType, query, oqo, oql }) {
            commit('setQueryObjectLoading', true);
            try {
                // When an `oqo` (or `oql`) is given, translate THAT exact query — used
                // in OQL-submit mode to render the name-annotated canonical OQL of the
                // executed query (#378 C4 / decision 14: display names are resolved by
                // this on-demand translate service, not baked into the execute response's
                // bare-ID meta.x_query.oql). Otherwise translate the live chip query.
                const response = await api.getQuery(
                    oqo != null || oql != null
                        ? { oqo, oql }
                        : { entity_type: entityType, query: query || {} }
                );
                commit('setQueryObject', response);
            } catch (e) {
                // /query returns 400 with a structured validation message on a bad
                // query — surface that, not the generic axios "status code 400".
                const msg = e.response?.data?.validation?.errors?.[0]?.message
                    || e.response?.data?.message
                    || e.message
                    || 'Failed to fetch query';
                commit('setQueryObjectError', msg);
            } finally {
                commit('setQueryObjectLoading', false);
            }
        },
        // #492 Phase 3: translate a flat OXURL filter set (+ the current query's
        // search/sort clauses) to canonical OQL via the /query service, so a Basic-mode
        // chip edit on an `?oql=` query can re-run as OQL — keeping the URL canonical —
        // instead of downgrading it to `?filter=`. url.js (which owns the chip-write
        // chokepoint, pushNewFilters) can't import api directly (api⇄url cycle), so the
        // translate goes through the store, which already imports api. Returns the oql
        // string, or null on a translate failure (the caller falls back to ?filter=,
        // which round-trips losslessly for a Basic-representable query).
        async translateFiltersToOql({ getters }, { entityType, filter, query }) {
            try {
                const resp = await api.getQuery({ entity_type: entityType, filter, query: query || {} });
                return pickOql(resp, getters.newOql);   // the form the URL shows (oxjob #1555)
            } catch (e) {
                return null;
            }
        },
    },
    getters: {
        apiDialogUrl(state) {
            return state.apiDialogUrl;
        },
        entityType(state) {
            return state.entityType;
        },
        entityConfig(state) {
            return entityConfigs[state.entityType];
        },
        showFiltersDrawer(state) {
            return state.showFiltersDrawer;
        },
        facetZoom(state) {
            return state.facetZoom;
        },
        globalIsLoading(state) {
            return state.isLoading;
        },
        results(state) {
            return state.results;
        },
        resultsCount(state) {
            return state?.resultsObject?.meta?.count;
        },
        // adds stuff just used by the ui
        textSearch(state) {
            return state.textSearch;
        },
        zoomId(state) {
            return state.zoomId;
        },
        filtersZoom(state) {
            return state.filtersZoom
        },
        facetsByCategory(state) {
            return function (searchString) {
                return facetsByCategory(state.entityType, searchString);
            };
        },
        isLocalEnv() {
            return hasBrowser && window.location.hostname === "localhost";
        },
        isStagingEnv() {
            return hasBrowser && window.location.hostname === "staging.openalex.org";
        },
        isProductionEnv() {
            return !hasBrowser || window.location.hostname === "openalex.org";
        },
        environment(getters) {
            if (getters.isLocalEnv) {
                return "local";
            } else if (getters.isStagingEnv) {
                return "staging";
            } else if (getters.isProductionEnv) {
                return "production";
            } else {
                return "unknown";
            }
        },
        plans(state) {
            return state.plans;
        },
        queryObject(state) {
            return state.queryObject;
        },
        queryObjectLoading(state) {
            return state.queryObjectLoading;
        },
        queryObjectError(state) {
            return state.queryObjectError;
        },
        oqlSubmitError(state) {
            return state.oqlSubmitError;
        },
        rateLimitData(state) {
            return state.rateLimitData;
        },
        defaultDailyBudgetUsd(state) {
            return creditsToUsd(state.defaultApiMaxPerDay);
        },
        featureFlags(state) {
            const flags = { ...state.featureFlags };
            delete flags.noviceMode;
            // OQL LAUNCH (oxjob #464 Phase 3/3b): `oql` is on for everyone —
            // anon included — unless the account opted into the legacy
            // interface via the `legacy_ui` Labs experiment (Settings -> Labs).
            // The old per-user `oql` beta flag is no longer consulted, and the
            // launch-day per-device localStorage opt-out is retired.
            flags.oql = !flags.legacy_ui;
            return flags;
        },
        // The new OQL (oxjob #1555): show `x_new_oql` wherever the site shows canonical OQL.
        // The one-mode search page (`nl_search`, oxjob #1744) always shows it: it reads like
        // English and carries the sort (", most cited first"), which today's form drops.
        newOql(state, getters) {
            return !!getters.featureFlags[NEW_OQL_FLAG] || !!getters.featureFlags[NL_SEARCH_FLAG];
        },
        // The one-mode search page: chat, editable OQL, three formats (oxjob #1744).
        nlSearch(state, getters) {
            return !!getters.featureFlags[NL_SEARCH_FLAG];
        },
        // The canonical OQL to show for the current results (see oqlForm.js); every display
        // and the URL projection read this, so a rewrite and the flag can't drift apart.
        shownOql(state, getters) {
            return pickOql(state.resultsObject?.meta?.x_query, getters.newOql);
        },
    },
})
}

let _store = null;
let _resolver = null;
let _serverFallbacks = 0;
export function setAppStore(s) { _store = s; }
// Server side (Nuxt SSR), a resolver returns the current request's store.
export function setStoreResolver(fn) { _resolver = fn; }
export function getAppStore() {
    const s = (_resolver && _resolver()) || _store;
    if (s) return s;
    if (!hasBrowser) {
        // #1689: on the server a module-level store would be shared by every request.
        _serverFallbacks++;
        if (_serverFallbacks <= 3) console.warn('[ox-store] no request store in context; using a throwaway store', new Error().stack.split('\n').slice(2, 6).join(' <- '));
        return createAppStore();
    }
    _store = createAppStore();   // browser: one store, created on first use
    return _store;
}
// Late-bound proxy: modules that `import store from '@/store'` keep working.
const store = new Proxy({}, {
    get(_t, prop) {
        const s = getAppStore();
        const v = s[prop];
        return typeof v === 'function' ? v.bind(s) : v;
    },
});
export default store;
